import { useContext, useRef } from 'react';

import {
  DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES,
  DreamComponentsContext,
  type DreamComponentsContextType,
  ImageIndexContext,
} from './context';

export const useDreamComponentsContext = (): DreamComponentsContextType => {
  const context = useContext(DreamComponentsContext) ?? DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES;
  return context;
};

export const useImagePriority = (
  hasImage = true,
): {
  fetchPriority?: 'high';
  loading?: 'eager' | 'lazy';
} => {
  const { imagePriorityThreshold } = useDreamComponentsContext();
  const getNextIndex = useContext(ImageIndexContext);
  const indexRef = useRef<number | null>(null);

  // Only increment the counter once per component mount.
  // Using a guard instead of useRef(getNextIndex()) to avoid
  // double-incrementing in React strict mode's double-render.
  // Skip incrementing when there's no image to avoid wasting a slot.
  if (indexRef.current === null && getNextIndex && hasImage) {
    indexRef.current = getNextIndex();
  }

  if (!imagePriorityThreshold || indexRef.current == null) {
    return {};
  }

  const isEager = indexRef.current <= imagePriorityThreshold;
  return {
    fetchPriority: isEager ? 'high' : undefined,
    loading: isEager ? 'eager' : 'lazy',
  };
};
